import {Model} from "./Model";
import {IApiLeague, IApiLeagueRestriction} from "../typings/apiResponse";
import {LeagueRestriction} from "./leagueRestriction/LeagueRestriction";
import {LeagueRestrictionFactory} from "./leagueRestriction/LeagueRestrictionFactory";

/**
 * @ngdoc overview
 *
 * @type {Object}
 * @name League
 *
 * @requires apiData
 *
 * @description
 * The league object is an abstract representation of a competition format on the ESL Play platform.
 *
 * Concrete implementations:
 * {@link Cup}
 */
export class LeagueState {
    static UNKNOWN: string = 'unknown';
    static SETUP: string = 'setup';
    static PRE_SIGN_UP: string = 'preSignUp';
    static SIGN_UP: string = 'signUp';
    static LATE_SIGN_UP: string = 'lateSignUp';
    static POST_SIGN_UP: string = 'postSignUp';
    static CHECK_IN: string = 'checkIn';
    static IN_PROGRESS: string = 'inProgress';
    static FINISHED: string = 'finished';
}

export class LeagueType {
    static CUP: string = 'cup';
}

export class LeagueSkillLevel {
    static OPEN: string = 'open';
    static MAJOR: string = 'major';
    static PRO_QUALIFIER: string = 'pro_qualifier';
    static PRO: string = 'pro';
}

export class League extends Model {
    constructor(private apiData: IApiLeague) {
        super(apiData);
    }

    public getId(): number {
        return this.apiData.id;
    }

    public getGameAccountType(): string {
        return this.apiData.gameAccountType;
    }

    public getName(): string {
        return this.apiData.name.short;
    }

    public getGameId(): number {
        return this.apiData.gameId;
    }

    public getMode(): string {
        return this.apiData.mode;
    }

    public getType(): string {
        return this.apiData.type;
    }

    public getResultType(): string {
        return this.apiData.resultType;
    }

    public getSkillLevel(): LeagueSkillLevel {
        return this.apiData.skillLevel;
    }

    /**
     * Returns whether or not the league has mapvote enabled. In a league with mapvote players need to ban and pick
     * maps before they can play the match.
     *
     * @returns {boolean}
     */
    public hasMapvote(): boolean {
        return this.apiData.mapvoteEnabled;
    }

    /**
     * Returns all league restrictions of this league or an empty array if the league does not have restrictions.
     * Note that these league restrictions are not evaluated against any user/team and therefor are all missing the
     * passing information.
     *
     * @returns LeagueRestriction[]
     */
    public getLeagueRestrictions(): LeagueRestriction[] {
        if (!this.apiData.restrictions) {
            return [];
        }

        return this.apiData.restrictions.map((leagueRestriction: IApiLeagueRestriction) => {
           return LeagueRestrictionFactory.getRestrictionByType(leagueRestriction);
        });
    }

    public getEnabledFeatures(): string[] {
        return this.apiData.enabledFeatures;
    }

    public getNumberOfRounds(): number {
        switch(this.getResultType()) {
            case "oneround_noshift":
                return 1;
            case "bestofthree":
                return 3;
            case "default_noshift":
                return 2;
            case "default":
                return 2;
            case "default_sum":
                return 2;
            default: return 1;
        }
    }

    /**
     * abstract function - needs to be overwritten in the exact implementation
     *
     * @returns {boolean}
     */
    isFull(): boolean {
        return false;
    }

    isCheckInAvailable(): boolean {
        return false;
    }

    isSignUpAvailable(): boolean {
        return false;
    }

    isPostSignUpAvailable(): boolean {
        return false;
    }

    hasLateSignUp(): boolean {
        return false;
    }

    hasSeason(): boolean {
        return false;
    }

    getStartDate():Date {
        return null;
    }

    getNextDate():Date {
        return null;
    }

    hasAutomaticGameIntegration(): boolean {
        return this.hasGameIntegration() && this.apiData.gameIntegration == 'lol';
    }

    hasGameIntegration(): boolean {
        return this.apiData.gameIntegration !== null && this.apiData.gameIntegration !== '';
    }

    isTeamLeague(): boolean {
        return this.apiData.teamSize >= 2;
    }

    getUri(): string {
        return this.apiData.uri;
    }

    needsGameAccount(): boolean {
        return this.apiData.gameAccountType != null && this.apiData.gameAccountType.length > 0;
    }

    getSecondsUntilDate(date) {
        var time = Math.ceil((date.getTime() - Date.now()) / 1000);
        if (time > 0) {
            return time;
        }

        return 0;
    }

    /**
     * Get seconds until the next-update
     */
    getSecondsUntilNextUpdate():number {

        var date = this.getNextDate();
        var nextUpdate = this.getSecondsUntilDate(date);

        return nextUpdate;
    }

    hasCountDown():boolean {
        return true;
    }

    getState():string {
        return '';
    }

    isInstantChallengerActive():boolean {
        return false;
    }

    getInstantChallengerInfo() {
        return undefined;
    }

    isSignUpEnabled(): boolean {
        return true;
    }

    isSignOffEnabled(): boolean {
        return this.apiData.signOff.enabled;
    }

    isInState(state: string): boolean {
        return false;
    }

    isAnticheatEnabled(): boolean {
        return this.apiData.anticheatEnabled;
    }
}